import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import { getTosConfig } from '../utils/termsConfig';


const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Session-level cache: once we've confirmed agreements are accepted, don't re-check
let agreementsVerified = sessionStorage.getItem('agreementsVerified') === 'true';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [checking, setChecking] = useState(!agreementsVerified);
  const [accepted, setAccepted] = useState(agreementsVerified);

  useEffect(() => {
    if (!currentUser || agreementsVerified) return;

    const checkAgreements = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setChecking(false);
          return;
        }

        const token = await user.getIdToken();
        // Check the CURRENT version from shop-svc config: raising it there re-prompts users here.
        // shop-svc treats this as "that version or newer". Fetched fresh, not from the page-load cache,
        // so a tab open across a ToS bump checks the new version at its next login.
        const { currentVersion } = await getTosConfig({ fresh: true });
        const types = `TERMS_OF_SERVICE:${currentVersion}`;

        const response = await fetch(
          `${SHOP_API_BASE_URL}/api/v1/user-agreements/check?types=${encodeURIComponent(types)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.allAccepted) {
            setAgreementsVerified();
            setAccepted(true);
          } else {
            setAccepted(false);
          }
        } else {
          // If the check endpoint fails, don't block the user — log and allow through
          console.error('[CSAI-SW] Agreement check failed with status:', response.status);
          setAgreementsVerified();
          setAccepted(true);
        }
      } catch (err) {
        // Network error — don't block the user
        console.error('[CSAI-SW] Agreement check error:', err);
        setAgreementsVerified();
        setAccepted(true);
      } finally {
        setChecking(false);
      }
    };

    checkAgreements();
  }, [currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (checking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full" style={{ marginBottom: '8px' }}></div>
          <div style={{ fontSize: '0.875rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!accepted) {
    return <Navigate to="/accept-terms" />;
  }

  return <>{children}</>;
};

// Reset the cache on logout so the next login re-checks
export function resetAgreementsCache() {
  agreementsVerified = false;
  sessionStorage.removeItem('agreementsVerified');
}

// Set the cache as verified (e.g., after signup records agreements)
export function setAgreementsVerified() {
  agreementsVerified = true;
  sessionStorage.setItem('agreementsVerified', 'true');
}

export default ProtectedRoute;
